"use client";

import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { useSession } from "next-auth/react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { getUserBalance } from "@/actions/reviews";

interface BalanceCardProps {
  balance?: number;
  className?: string;
  onAddFunds?: () => void;
}

export default function BalanceCard({
  balance: initialBalance,
  className,
  onAddFunds,
}: BalanceCardProps) {
  const { data: session, status } = useSession();
  const [balance, setBalance] = useState<number>(initialBalance ?? 0);

  const fetchBalance = () => {
    getUserBalance()
      .then((val) => {
        setBalance(val);
      })
      .catch(() => {});
  };

  useEffect(() => {
    if (status === "authenticated") {
      fetchBalance();
    } else {
      setBalance(0);
    }
  }, [status, session?.user?.id]);

  useEffect(() => {
    const handleUpdate = () => {
      fetchBalance();
    };
    window.addEventListener("balance-updated", handleUpdate);
    return () => {
      window.removeEventListener("balance-updated", handleUpdate);
    };
  }, []);

  const handleAddFunds = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onAddFunds) {
      onAddFunds();
    } else {
      toast.info("Instant balance deposits will be available with crypto & card gateway.", {
        description: `Your current available balance is $${balance.toFixed(2)}.`,
      });
    }
  };

  if (status !== "authenticated" || !session?.user) {
    return null;
  }

  const formattedBalance = `$${balance.toFixed(2)}`;

  return (
    <div
      className={cn(
        "flex h-10 items-center rounded-xl border border-gray-200 bg-white px-3 shadow-2xs dark:border-neutral-800 dark:bg-neutral-900/90 select-none transition-colors gap-2.5",
        className
      )}
    >
      {/* Balance text */}
      <div className="flex flex-col text-left leading-none">
        <span className="text-[10px] font-medium leading-none text-gray-500 dark:text-neutral-400">
          Balance
        </span>
        <span className="mt-0.5 text-[13.5px] font-semibold leading-none tracking-tight text-gray-950 dark:text-white font-mono">
          {formattedBalance}
        </span>
      </div>

      {/* Add button */}
      <button
        type="button"
        onClick={handleAddFunds}
        aria-label="Add balance"
        className="ml-1 flex size-5 shrink-0 items-center justify-center rounded-full border border-gray-100 bg-white text-gray-700 shadow-2xs transition-all hover:bg-gray-50 active:scale-95 cursor-pointer dark:border-neutral-800 dark:bg-neutral-800 dark:text-neutral-200 dark:hover:bg-neutral-700"
      >
        <Plus className="size-3 stroke-[2.2]" />
      </button>
    </div>
  );
}

export { BalanceCard, BalanceCard as BalanceWidget };
